import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { ApiError, api, type Me } from '../api/client';
import { Button, Field, Loading, Notice } from '../components/ui';
import { homeFor, pathFor, useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { VersionNote } from '../components/Shell';
import { YourPhoto } from '../components/YourPhoto';
import { usePictureOf } from '../lib/pictures';

/** The frame every signed-out page shares: the brand, then one card. */
export function AuthFrame({ title, intro, children, wide }: { title: string; intro?: ReactNode; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    document.title = `${title} · Stitch`;
  }, [title]);
  return (
    <div className="access">
      <div className={`access-card${wide ? ' wide' : ''}`}>
        <div className="brand" aria-hidden="true" style={{ alignSelf: 'flex-start' }}>
          <span className="brand-word">Stitch</span>
          <span className="brand-bar" />
        </div>
        <div className="access-head">
          <h1>{title}</h1>
          {intro && <span className="muted">{intro}</span>}
        </div>
        {children}
      </div>
      <Link to="/help/your-account" className="access-help">
        <Icon name="help" size={15} /> Help with signing in
      </Link>
      <VersionNote />
    </div>
  );
}

const DEMO = [
  { as: 'staff' as const, title: 'Stitch staff', sub: 'Every customer, onboarding and the phone system' },
  { as: 'admin' as const, title: 'A customer admin', sub: 'Runs their company’s phones' },
  { as: 'user' as const, title: 'An end user', sub: 'Their own phone, queues and calls' }
];

/**
 * The other side, from an answer that points there: the portal, from Stitch Admin's own container (a
 * company account signing in, or a link of theirs opened there); Stitch Admin, from the portal's (a
 * Stitch team account, or a link for one). They're separate accounts, even with one email (1.12.20).
 */
function elsewhereOf(err: unknown): { url: string; label: string } | null {
  if (!(err instanceof ApiError)) return null;
  const at = (v: unknown) => (typeof v === 'string' && /^https?:\/\//.test(v) ? v : null);
  if (err.code === 'STAFF_ONLY') {
    const portal = at(err.body.portalUrl);
    return portal ? { url: portal, label: 'Go to Stitch' } : null;
  }
  if (err.code === 'TEAM_ONLY') {
    const admin = at(err.body.adminUrl);
    return admin ? { url: admin, label: 'Go to Stitch Admin' } : null;
  }
  return null;
}

export function SignIn() {
  const { state, signedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState('');
  /** Where to go instead (a company account in Stitch Admin's own container, or a team account in the portal's). */
  const [elsewhere, setElsewhere] = useState<{ url: string; label: string } | null>(null);
  const expired = new URLSearchParams(location.search).get('expired');
  // One address for both (no STITCH_SURFACE): which account, when someone has both. The company
  // account unless the Stitch team one is asked for (from Stitch Admin's pages, or the link below);
  // not asking lets Stitch take the team account for someone who has only that one.
  const [team, setTeam] = useState(new URLSearchParams(location.search).get('account') === 'team');

  if (!state) return <Loading label="Loading" />;
  if (state.signedIn) return <Navigate to={homeFor(state)} replace />;

  const done = (me: Me & { notice?: string }) => {
    signedIn(me);
    navigate(homeFor(me), { replace: true });
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setElsewhere(null);
    setBusy(true);
    try {
      if (forgot) {
        const r = await api.forgot(email.trim(), both && team ? 'team' : undefined);
        setSent(r.message);
      } else {
        done(await api.signIn(email.trim(), password, both && team ? 'team' : undefined));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Couldn’t reach Stitch. Try again.');
      setElsewhere(elsewhereOf(err));
    } finally {
      setBusy(false);
    }
  }

  async function demo(as: 'staff' | 'admin' | 'user') {
    setBusy(true);
    try {
      const me = await api.demoSignIn(as);
      signedIn(me);
      const m = me.memberships[0];
      navigate(me.user.staff ? '/admin' : m ? pathFor(m) : '/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setElsewhere(elsewhereOf(err));
    } finally {
      setBusy(false);
    }
  }

  // Stitch Admin's own container is for staff (its demo, too); the portal's, for companies.
  const demos = DEMO.filter((d) => (state.surface === 'admin' ? d.as === 'staff' : state.surface === 'portal' ? d.as !== 'staff' : true));
  const both = state.surface === 'all' || state.surface === undefined;
  const adminSide = state.surface === 'admin' || (both && team);
  return (
    <AuthFrame
      title={forgot ? (adminSide ? 'Reset your Stitch team password' : 'Reset your password') : adminSide ? 'Sign in to Stitch Admin' : 'Sign in'}
      intro={forgot ? 'We’ll email you a link to choose a new one.' : adminSide ? 'For the Stitch team: your Stitch team account, not your company one.' : undefined}
    >
      {expired && !forgot && (
        <Notice tone="warn" title="You were signed out">
          Sign in again to carry on.
        </Notice>
      )}
      {state.setup && (
        <Notice tone="brand" title="First time here?">
          Open the setup link from the server log to create the first Stitch staff account.
        </Notice>
      )}
      {sent ? (
        <>
          <Notice tone="ok">{sent}</Notice>
          <Button variant="secondary" onClick={() => (setForgot(false), setSent(''))}>
            Back to sign in
          </Button>
        </>
      ) : (
        <form onSubmit={submit} noValidate className="access-form">
          <Field label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required data-autofocus />
          {!forgot && <Field label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />}
          {error && <Notice tone="crit">{error}</Notice>}
          {elsewhere && (
            <a className="btn secondary" href={`${elsewhere.url}/sign-in`}>
              {elsewhere.label}
            </a>
          )}
          <Button variant="primary" type="submit" disabled={busy || !email.trim() || (!forgot && !password)}>
            {busy ? 'One moment…' : forgot ? 'Send the link' : 'Sign in'}
          </Button>
          <button type="button" className="link-btn" onClick={() => (setForgot(!forgot), setError(''))}>
            {forgot ? 'Back to sign in' : 'Forgot your password?'}
          </button>
          {both && (
            <button type="button" className="link-btn" onClick={() => (setTeam(!team), setError(''), setElsewhere(null))}>
              {team ? 'Sign in to your company instead' : 'On the Stitch team? Sign in to Stitch Admin'}
            </button>
          )}
        </form>
      )}
      {state.demo && !forgot && demos.length > 0 && (
        <div className="demo-choices">
          <span className="field-label">Or try the demo as</span>
          {demos.map((d) => (
            <button key={d.as} type="button" className="radio-card demo-card" disabled={busy} onClick={() => void demo(d.as)}>
              <span className="cell-stack">
                <strong>{d.title}</strong>
                <small>{d.sub}</small>
              </span>
              <Icon name="chevRight" size={16} />
            </button>
          ))}
          <span className="hint">Demo data only: nothing here touches a real phone system.</span>
        </div>
      )}
    </AuthFrame>
  );
}

/** Several companies: choose one. */
export function Choose() {
  const { state } = useAuth();
  if (!state) return <Loading />;
  if (!state.signedIn) return <Navigate to="/sign-in" replace />;
  const ms = (state.memberships ?? []).filter((m) => m.tenant);
  return (
    <AuthFrame title="Choose a company" intro={`Signed in as ${state.user?.email ?? ''}`}>
      <div className="demo-choices">
        {ms.map((m) => (
          <Link key={m.customerId} to={pathFor(m)} className="radio-card demo-card">
            <span className="cell-stack">
              <strong>{m.customerName}</strong>
              <small>{m.role === 'admin' ? 'Admin' : `Your phone${m.extension ? ` · ext ${m.extension}` : ''}`}</small>
            </span>
            <Icon name="chevRight" size={16} />
          </Link>
        ))}
      </div>
    </AuthFrame>
  );
}

/** Signed in, but the company isn't set up in Stitch yet (or, in the portal, Stitch staff in no company). */
export function NotReady() {
  const { state, signOut } = useAuth();
  if (!state) return <Loading />;
  if (!state.signedIn) return <Navigate to="/sign-in" replace />;
  const admin = state.user?.staffAccount ? state.links?.admin : undefined;
  return (
    <AuthFrame title={state.user?.staffAccount ? 'Nothing here yet' : 'Almost there'} intro={`Signed in as ${state.user?.email ?? ''}`}>
      {state.user?.staffAccount ? (
        <Notice tone="brand">You’re on the Stitch team, and not in a company here. Stitch Admin has its own address.</Notice>
      ) : (
        <Notice tone="brand">Your company isn’t ready in Stitch yet. Stitch will let you know as soon as it is.</Notice>
      )}
      {admin && <StitchAdminButton url={admin} />}
      <Button variant="secondary" onClick={() => void signOut()}>
        Sign out
      </Button>
    </AuthFrame>
  );
}

/** Stitch Admin, in its own tab (its own address: the portal's session stays as it is). */
function StitchAdminButton({ url, path = '/admin' }: { url: string; path?: string }) {
  return (
    <a className="btn primary" href={`${url}${path}`} target="_blank" rel="noopener">
      <Icon name="server" size={16} /> Open Stitch Admin
    </a>
  );
}

/**
 * Stitch Admin's pages in the portal of a split Stitch: not here (the server answered 404). Staff
 * get the way to Stitch Admin, at the same page; anyone else just the way home.
 */
export function NotHere() {
  const { state } = useAuth();
  const location = useLocation();
  if (!state) return <Loading />;
  const admin = state.user?.staffAccount ? state.links?.admin : undefined;
  // The page itself, never a secret after "#" (a setup link): Stitch Admin's own log has that one.
  const path = location.pathname.startsWith('/setup') ? '/admin' : location.pathname;
  return (
    <AuthFrame title={admin ? 'Stitch Admin has its own address' : 'This page isn’t here'}>
      {admin ? <Notice tone="brand">Stitch Admin opens in its own tab, and you stay signed in here.</Notice> : <Notice tone="warn">There’s nothing at this address.</Notice>}
      {admin && <StitchAdminButton url={admin} path={path} />}
      <Link to="/" className="btn secondary">
        Go to Stitch
      </Link>
    </AuthFrame>
  );
}

/** Invitation and password reset links: the secret is after "#", so it never reaches a server log. */
export function LinkPage({ purpose }: { purpose: 'invite' | 'reset' }) {
  const { state, signedIn, signOut } = useAuth();
  const navigate = useNavigate();
  const token = typeof window === 'undefined' ? '' : window.location.hash.slice(1);
  const [info, setInfo] = useState<Awaited<ReturnType<typeof api.linkInfo>> | null>(null);
  const [problem, setProblem] = useState('');
  /** A link for the other side of a split Stitch: where it opens instead. */
  const [elsewhere, setElsewhere] = useState<{ url: string; label: string } | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** A new account: their photo next, then here. */
  const [photoNext, setPhotoNext] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setProblem('This link is incomplete. Open it straight from the email, or ask for a new one.');
      return;
    }
    api
      .linkInfo(token)
      .then((i) => {
        setInfo(i);
        setName(i.name);
      })
      .catch((e: unknown) => {
        setProblem(e instanceof Error ? e.message : String(e));
        setElsewhere(elsewhereOf(e));
      });
  }, [token]);

  if (photoNext) return <PhotoStep next={photoNext} />;
  if (problem) {
    return (
      <AuthFrame title={purpose === 'invite' ? 'Invitation' : 'Reset your password'}>
        <Notice tone="warn">{problem}</Notice>
        {elsewhere ? (
          // The same link, on its own side (both addresses are Stitch's own settings).
          <a className="btn primary" href={`${elsewhere.url}/${purpose}#${token}`}>
            {elsewhere.label === 'Go to Stitch Admin' ? 'Open it in Stitch Admin' : 'Open it in Stitch'}
          </a>
        ) : (
          <Link to="/sign-in" className="btn secondary">
            Go to sign in
          </Link>
        )}
      </AuthFrame>
    );
  }
  if (!info) return <Loading label="Checking the link" />;

  // An existing account accepts by signing in as itself (the normal sign-in, with its limits), never by the link alone.
  const joining = info.purpose === 'invite' && info.existing;
  const choosing = !joining;
  const current = state?.signedIn ? state.user : undefined;
  // The account the link is for: the Stitch team one or the company one (separate, even with one email).
  const forTeam = info.role === 'staff';
  const asThem = joining && current?.email.toLowerCase() === info.email.toLowerCase() && Boolean(current?.staff) === forTeam;
  const asSomeoneElse = joining && current && !asThem;
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (choosing && password !== confirm) return setError('The two passwords don’t match.');
    setBusy(true);
    try {
      if (joining && !asThem) signedIn(await api.signIn(info!.email, password, forTeam ? 'team' : 'company'));
      const me = await api.accept(joining ? { token } : { token, password, ...(info?.purpose === 'invite' ? { name: name.trim() } : {}) });
      // The secret has done its job: keep it out of the address bar and history.
      window.history.replaceState(null, '', window.location.pathname);
      signedIn(me);
      // Someone new: their photo, so coworkers know it's them (Skip is fine).
      if (info?.purpose === 'invite' && !joining) setPhotoNext(homeFor(me));
      else navigate(homeFor(me), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const roleWords = info.role === 'staff' ? 'on the Stitch team' : info.role === 'admin' ? `as an admin of ${info.place}` : `to ${info.place}`;
  if (asSomeoneElse) {
    return (
      <AuthFrame title={`Join ${info.place}`} intro={`This invitation is for ${info.email}.`}>
        <Notice tone="warn">You’re signed in as {current!.email}. Sign out, then sign in as {info.email} to accept it.</Notice>
        <Button variant="secondary" onClick={() => void signOut()}>
          Sign out
        </Button>
      </AuthFrame>
    );
  }
  return (
    <AuthFrame
      title={info.purpose === 'reset' ? 'Choose a new password' : joining ? `Join ${info.place}` : `Welcome to Stitch`}
      intro={
        info.purpose === 'reset'
          ? info.email
          : asThem
            ? `You’ve been invited ${roleWords}. You’re signed in as ${info.email}.`
            : joining
              ? `You already have a Stitch account (${info.email}). Sign in with it to accept.`
              : `You’ve been invited ${roleWords}.`
      }
    >
      <form onSubmit={submit} noValidate className="access-form">
        {info.purpose === 'invite' && !joining && <Field label="Your name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required />}
        {info.purpose === 'invite' && !joining && <Field label="Email" value={info.email} readOnly autoComplete="username" />}
        {joining && !asThem && <input type="hidden" name="username" autoComplete="username" value={info.email} readOnly />}
        {!asThem && (
          <Field
            label={joining ? 'Your Stitch password' : 'New password'}
            type="password"
            autoComplete={joining ? 'current-password' : 'new-password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint={joining ? undefined : 'At least 10 characters. A short sentence works well.'}
            data-autofocus
          />
        )}
        {joining && !asThem && (
          <Link to="/sign-in" className="link-btn">
            Forgot your password?
          </Link>
        )}
        {choosing && <Field label="Type it again" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
        {error && <Notice tone="crit">{error}</Notice>}
        <Button variant="primary" type="submit" disabled={busy || (!asThem && !password) || (info.purpose === 'invite' && !joining && !name.trim())}>
          {busy ? 'One moment…' : info.purpose === 'reset' ? 'Save and sign in' : asThem ? 'Accept' : joining ? 'Sign in and accept' : 'Create my account'}
        </Button>
      </form>
    </AuthFrame>
  );
}

/** The invitation's last step: a photo, so coworkers know it's them. */
function PhotoStep({ next }: { next: string }) {
  const navigate = useNavigate();
  const { state } = useAuth();
  const version = usePictureOf(state?.user?.id);
  const phone = (state?.memberships ?? []).some((m) => m.extension);
  return (
    <AuthFrame
      title="Add your photo"
      intro={phone ? 'So your coworkers know it’s you: it shows in Stitch, and on their desk phones when you call them.' : 'So your coworkers know it’s you in Stitch.'}
    >
      <YourPhoto autoFocus />
      <Button variant={version ? 'primary' : 'ghost'} onClick={() => navigate(next, { replace: true })}>
        {version ? 'Continue' : 'Skip for now'}
      </Button>
    </AuthFrame>
  );
}

/** First run: the link from the server log creates the first Stitch staff account. */
export function Setup() {
  const { signedIn } = useAuth();
  const navigate = useNavigate();
  const token = typeof window === 'undefined' ? '' : window.location.hash.slice(1);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) return setError('The two passwords don’t match.');
    setBusy(true);
    try {
      const me = await api.setup({ token, email: form.email.trim(), name: form.name.trim(), password: form.password });
      window.history.replaceState(null, '', window.location.pathname);
      signedIn(me);
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <AuthFrame title="Set up Stitch" intro="Create the first Stitch staff account. You can invite the rest of the team afterwards.">
      {!token && <Notice tone="warn">Open the setup link exactly as the server log shows it.</Notice>}
      <form onSubmit={submit} noValidate className="access-form">
        <Field label="Your name" value={form.name} onChange={set('name')} autoComplete="name" data-autofocus />
        <Field label="Email" type="email" value={form.email} onChange={set('email')} autoComplete="username" />
        <Field label="Password" type="password" value={form.password} onChange={set('password')} autoComplete="new-password" hint="At least 10 characters." />
        <Field label="Type it again" type="password" value={form.confirm} onChange={set('confirm')} autoComplete="new-password" />
        {error && <Notice tone="crit">{error}</Notice>}
        <Button variant="primary" type="submit" disabled={busy || !token || !form.name || !form.email || !form.password}>
          {busy ? 'One moment…' : 'Create the account'}
        </Button>
      </form>
    </AuthFrame>
  );
}
